import { CleanButton, DownloadIcon, theme, Tooltip } from '@arnold/common';
import { getLocale } from '@arnold/core';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import i18n from 'i18next';
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { AnonymityLevel, Features, ReportSequenceRespondentDetailQueryQuery } from '../../generated/hooks';
import { IReport } from '../../lib/reports/base';
import { IMessage } from './RespondentsAnswers';
import { ConversationMessages, MessageReaction } from './ConversationMessages';
import { ReactionModal } from './ReactionModal';
import auth from '../../lib/auth';
import { useUser } from '../../components/providers/UserProvider';

interface IProps {
  messages: IMessage[];
  report: IReport | ReportSequenceRespondentDetailQueryQuery['getProcessReportRespondentDetail'];
  name: string;
  team: string | React.ReactNode;
  startsAt?: Date | null;
  endsAt: Date | null;
  notFinished?: boolean;
  allowedAnonymities?: AnonymityLevel[];
  exportPdf?: () => void;
  reload?: () => void;
}

const ConversationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const Conversation = ({
  messages,
  report,
  team,
  name,
  startsAt,
  endsAt,
  notFinished,
  allowedAnonymities,
  exportPdf,
  reload,
}: IProps) => {
  const { t } = useTranslation('Report');
  const [showReactionModal, setShowReactionModal] = useState(false);
  const response = report.responses?.find((r) => r.messages?.some((m) => m.id === messages[0]?.id));
  const channelId = response?.id;
  const reactionsToWholeConversation = response?.reactions?.filter((r) => !r.messageId);
  const { user } = useUser();
  const isManagersReportOrLoggedIn = !!user?.user || !!report.metadata?.respondent;
  return (
    <>
      <ConversationHeader className={'bg-white rounded-lg shadow-sm px-8 py-6 mb-1'} data-cy="report-answers-header">
        <div>
          <h4>{name}</h4>
          <p className={'font-size-xs mb-0'}>
            {startsAt && <span>{format(new Date(startsAt), 'PPPp', { locale: getLocale(i18n.language) })}</span>}
            {endsAt && (
              <span>
                {' '}
                {startsAt ? ' - ' : ''}
                {format(endsAt, 'PPPp', { locale: getLocale(i18n.language) })}
              </span>
            )}

            <span className={startsAt || endsAt ? 'ml-6' : ''}>{team}</span>
          </p>
        </div>
        <div className="d-flex align-items-center">
          {exportPdf && messages.length > 0 && (
            <Tooltip title={t('downloadTopicAsPdfTooltip')}>
              <CleanButton onClick={exportPdf} data-icom="report-download-topic-as-pdf">
                <DownloadIcon />
              </CleanButton>
            </Tooltip>
          )}
        </div>
      </ConversationHeader>

      <div className={'bg-white rounded-lg shadow-sm px-8 py-8'} data-cy="report-answers-body">
        <div>
          <ConversationMessages messages={messages} report={report} reload={reload} />

          {messages.length !== 0 && !!reactionsToWholeConversation?.length && (
            <ReactionsToConversation
              data-cy="reaction-to-whole-conversation"
              data-icom="reaction-to-whole-conversation"
            >
              {reactionsToWholeConversation.map((r) => (
                <MessageReaction key={`reaction-${r.id}`} reaction={r} />
              ))}
            </ReactionsToConversation>
          )}
          {messages.length !== 0 &&
            report.metadata?.organization.featureNames?.includes(Features.ReportReactionToWholeConversation) && (
              <ReactionPrompt data-icom="reaction-prompt">
                <div>
                  <Trans i18nKey={'Report:reactBannerLabel'} components={[<strong />]} />
                </div>
                <WriteReactionButton
                  onClick={isManagersReportOrLoggedIn ? () => setShowReactionModal(true) : () => auth.redirectToLogin()}
                  disabled={!channelId}
                  data-cy="reaction-on-whole-conversation-button"
                >
                  {t('sendReactionLabel')}
                </WriteReactionButton>
              </ReactionPrompt>
            )}
          {showReactionModal && channelId && (
            <ReactionModal
              accessToken={report.identifier}
              channelId={channelId}
              onClose={() => setShowReactionModal(false)}
              reload={reload}
              dataCy="reaction-modal-whole-conversation"
            />
          )}
          {messages.length === 0 && (
            <span className={'no-data'}>
              {t(
                notFinished
                  ? 'withoutResponseForNow'
                  : allowedAnonymities?.includes(AnonymityLevel.Organization)
                    ? 'anonymousResponse'
                    : 'noResponse',
              )}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

const ReactionsToConversation = styled.div`
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing.d};
`;

const ReactionPrompt = styled.div`
  display: flex;
  max-width: 456px;
  margin-top: ${theme.spacing.f};
  margin-left: auto;
  align-items: center;
  justify-content: space-between;
  border-radius: ${theme.borderRadius.l};
  padding: ${theme.spacing.f};
  background-color: ${theme.colors.brand.primary};
  color: ${theme.colors.textInverted.primary};

  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
    gap: ${theme.spacing.d};
  }
`;

const WriteReactionButton = styled(Button)`
  color: ${theme.colors.actionPrimary.default} !important;
  background-color: ${theme.colors.backgroundCover.default} !important;
  border: 0px !important;
  padding: ${theme.spacing.c} ${theme.spacing.f};
  border-radius: ${theme.borderRadius.xxl};
`;
